import React from 'react'
import GradientText from '@/scenes/Editor/components/GradientText/GradientText'
import { lightTheme } from '@/customTheme'
import { hexToRgba } from '@/utils/color'

export type BadgeStyle =
  /** Pink brand gradient wash, gradient label. */
  | 'gradient'
  /** Grey chip, secondary label. */
  | 'info'
  /**
   * Purple pill: vivid purple label on a 10% tint of the same color. Another
   * color belongs here as its own style (e.g. `'pink'`), not as a color prop —
   * the palette stays in the design system, not at call sites.
   */
  | 'purple'

interface Props {
  text: string
  mainStyle?: BadgeStyle
  /**
   * Fixed pill width in px. The label truncates with an ellipsis inside it —
   * use when the badge sits on a design grid and a longer translation must not
   * push the layout around.
   */
  width?: number
  uppercase?: boolean
}

const CONTAINER_STYLES: Record<BadgeStyle, React.CSSProperties> = {
  gradient: {
    backgroundImage:
      'linear-gradient(111deg, rgba(255,24,102,0.10) 6.04%, rgba(253,1,103,0.10) 22.35%, rgba(240,1,136,0.10) 43.65%, rgba(221,1,160,0.10) 53.87%, rgba(210,3,180,0.10) 67.14%, rgba(193,14,196,0.10) 84.18%, rgba(153,1,156,0.10) 105.87%)',
    borderRadius: '100px',
    padding: '1.5px 6.5px',
  },
  info: {
    background: lightTheme.colors.grayScale50,
    borderRadius: '4px',
    padding: '1px 5px',
  },
  purple: {
    background: hexToRgba(lightTheme.colors.purpleVivid, 0.1),
    borderRadius: '100px',
    padding: '0 10px',
    height: '28px',
  },
}

const TEXT_STYLES: Record<BadgeStyle, React.CSSProperties> = {
  gradient: { fontSize: '12px', fontWeight: 500, lineHeight: 'normal', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  info: { fontSize: '11px', fontWeight: 500, lineHeight: '16px', color: lightTheme.colors.text.text_600_default, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  purple: {
    ...lightTheme.typography.Small12medium,
    color: lightTheme.colors.purpleVivid,
    lineHeight: 'normal',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}

function Badge({ text, mainStyle = 'gradient', width, uppercase = false }: Props) {
  // Without `width` the pill sizes to its label, so a longer translation stays
  // readable; with it the pill is pinned and the label ellipsizes instead.
  const containerStyle: React.CSSProperties = {
    ...CONTAINER_STYLES[mainStyle],
    ...(width != null ? { width: `${width}px`, flexShrink: 0 } : {}),
    boxSizing: 'border-box',
  }

  const textStyle: React.CSSProperties = {
    ...TEXT_STYLES[mainStyle],
    ...(uppercase ? { textTransform: 'uppercase' } : {}),
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 1,
        minWidth: 0,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        ...containerStyle,
      }}
    >
      {mainStyle === 'gradient' ? (
        <GradientText style={textStyle}>{text}</GradientText>
      ) : (
        <span style={textStyle}>{text}</span>
      )}
    </div>
  )
}

export default Badge
